import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
} from '@salescore/core'
import { App } from 'antd'
import { Array, Option } from 'effect'
import { t } from 'i18next'
import { useCallback } from 'react'

import type { RSheetColumn, RSheetRecordNode } from '../../domain/column'
import type { RSheetCursorContextMenuPosition, RSheetsCursorPositionByRecordId } from '../../domain/cursor/cursorTypes'
import { useUpsertViewRecordsEntry } from '../../hooks/useUpsertViewRecordsEntry'
import { useCursorMutation, useGetCursorSnapshot } from '../../recoil/cursor'
import { useRecordsImpl } from '../../recoil/records'
import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'
import { getCellRelated } from '../util/getCellValue'
import { getEditTargetModelName } from '../util/getEditTargetModelName'
import { useChangeSystemField } from './useChangeSystemField'

export interface SetMainCursorByRecordIdArguments {
  position: RSheetsCursorPositionByRecordId
  option: {
    expand: boolean
    isEditing?: boolean
    toggle?: boolean
    contextMenuPosition?: RSheetCursorContextMenuPosition
    disabled?: boolean
    keepExpand?: boolean
  }
}

export const useSetMainCursorByRecordId = (
  fixedLeftColumnIndex: number,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList | undefined,
  visibleRowNumber: number | undefined,
  // eslint-disable-next-line @typescript-eslint/max-params
): ((arguments_: SetMainCursorByRecordIdArguments) => void) => {
  const rows = useRecordsImpl()
  const getCursorSnapshot = useGetCursorSnapshot()
  const { onAfterToggleCheckbox, onSetMainCursor, expand } = useCursorMutation(ability)
  const { message } = App.useApp()
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)
  const setChangeSystemField = useChangeSystemField(
    fixedLeftColumnIndex,
    columns,
    compiledConfig,
    // query が undefined なのは kpi/kpiPivot view 経由で mount された場合のみ。
    // kpi/kpiPivot の column 型では useChangeSystemField 内の 'open' 分岐 (streamName を
    // 参照する箇所) に到達しないため、undefined を渡しても実害なし。
    query?.tree.meta.dependedStreamNames[0],
    visibleRowNumber,
  )

  //
  // 新規行追加時に全ての行でレンダリングが走らないよう、RSheetRowをメモ化しており、rowIndexをキャッシュのキーに含めていない。
  // これによりセルに渡されるrowIndexが古いことがあり、クリック時などに普通にrowIndexを渡してsetCursorしようとすると間違ったindexになりうるため
  // rowIndexの代わりにrecordIdを渡し、最新の行インデックスを取得してカーソルをセットする。
  // メモ化の影響を受けないコンポーネントからの入力であれば、このmutationを使う必要はない。
  //

  return useCallback(
    // eslint-disable-next-line complexity,sonarjs/cognitive-complexity -- Redux 移行のため複雑性を一時的に許容する
    ({ position, option }: SetMainCursorByRecordIdArguments): void => {
      const column = columns[position.columnIndex]
      if (column === undefined) {
        return
      }

      const rowWithIndex = Array.findFirstWithIndex(rows, (x) => x.id === position.recordId).pipe(Option.getOrUndefined)
      if (rowWithIndex === undefined) {
        return
      }
      const [row, rowIndex] = rowWithIndex
      const newMainCursor = { rowIndex, columnIndex: position.columnIndex, innerRowIndex: position.innerRowIndex }
      const cursor = getCursorSnapshot()
      const isSamePosition = [
        cursor?.main.rowIndex === rowIndex,
        cursor?.main.columnIndex === position.columnIndex,
        cursor?.main.innerRowIndex === position.innerRowIndex,
      ].every(Boolean)
      const isEditingByToggle = option.toggle === true && cursor?.expand === undefined ? isSamePosition : undefined
      const isEditing = option.isEditing ?? isEditingByToggle ?? false

      if (option.expand) {
        expand(newMainCursor)
        return
      }

      //
      // eslint-disable-next-line sonarjs/todo-tag
      // 以下、clickCurrentCursorMutationよりコピペ。TODO: 共通化
      //

      const { value, recordNode } = getCellRelated(column, row, newMainCursor.innerRowIndex)

      // rowCheckboxであれば、編集モードにするのではなく、直接値をtoggleする
      const toggledValue = typeof value === 'boolean' ? !value : true
      // isSystem == true なら、(column.type == checkbox | rowCheckbox | boolean | kpi) の場合に setChangeSystemField()
      // コンテキストメニュー無しなら、(column.type == checkbox | boolean | kpi) の場合に setChangeSystemField()
      if (
        column.type !== undefined &&
        ((column.isSystem === true && ['checkbox', 'rowCheckbox', 'boolean', 'kpi'].includes(column.type)) ||
          (option.contextMenuPosition === undefined && ['checkbox', 'boolean', 'kpi'].includes(column.type)))
      ) {
        setChangeSystemField({
          value: toggledValue,
          row,
          innerRowIndex: position.innerRowIndex,
          column,
        })
        onAfterToggleCheckbox(newMainCursor, option.keepExpand)
        return
      }
      // レコードURLであれば、リンクを開く
      if (column.metaType === 'record_url' && typeof value === 'string' && option.contextMenuPosition === undefined) {
        window.open(value)
        return
      }

      // open系であればonChangeする
      if (column.type === 'open' && (column.isSystem === true || option.contextMenuPosition === undefined)) {
        setChangeSystemField({
          value,
          row,
          innerRowIndex: position.innerRowIndex,
          column,
        })
        return
      }
      //
      // コピペ終わり。
      //

      if (isEditing) {
        if (column.readonly === true) {
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
          void message.warning(t(`この列は書き込み禁止です`))
          return
        }
        if (option.disabled === true) {
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
          void message.warning(t(`このセルは書き込み禁止です`))
          return
        }
        const editTargetModelName = getEditTargetModelName({ column, recordNode, query })
        if (!ability.canEditModelRecord(editTargetModelName)) {
          void message.warning(t(`このシートのレコードを編集する権限がありません`))
          return
        }
      }
      if (column.type === 'rowCheckbox' && column.field !== undefined) {
        // チェックボックスのチェックを更新
        upsertViewRecordsEntry({
          recordChanges: [
            {
              rowIndex,
              innerRowIndex: position.innerRowIndex,
              isNestedColumn: column.node.path.length > 1,
              nodePath: column.node.path,
              field: column.field,
              value: toggledValue,
              label: undefined,
            },
          ],
        })
        onAfterToggleCheckbox(newMainCursor, option.keepExpand)
        return
      }
      onSetMainCursor(columns, newMainCursor, isEditing, option)
    },
    [
      columns,
      rows,
      getCursorSnapshot,
      onSetMainCursor,
      expand,
      setChangeSystemField,
      onAfterToggleCheckbox,
      message,
      upsertViewRecordsEntry,
      ability,
      query,
    ],
  )
}
